import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { FormControl, InputLabel, Input, OutlinedInput, InputBaseComponentProps } from '@mui/material'

export interface Props {
  name: string
  type: string
  label: JSX.Element | string
  placeholder?: string
  register?: (instance: any) => void
  watch?: (name: string) => any
  required?: boolean
  fullWidth?: boolean
  autoFocus?: boolean
  autoComplete?: string
  inputProps?: InputBaseComponentProps
  disabled?: boolean
  value?: string
  outlined?: boolean
  className?: string
  maxCharactersSoftLimit?: number
  testId?: string
  maxLength?: number
}

export default function InputField(props: Props): JSX.Element {
  const {
    name,
    type,
    label,
    placeholder,
    register,
    watch,
    required,
    fullWidth,
    autoFocus,
    autoComplete,
    inputProps,
    disabled,
    value,
    outlined,
    className,
    maxCharactersSoftLimit,
    testId,
    maxLength,
  } = props
  const intl = useIntl()
  const watchedValue = watch ? watch(name) : undefined
  const currentValue = watchedValue !== undefined ? String(watchedValue || '') : String(value || '')

  const ContainerVariant = outlined ? InputContainer : FormControl
  const LabelVariant = outlined ? FieldLabel : InputLabel
  const InputVariant = outlined ? OutlinedInput : Input

  const getInputProps = () => {
    const currentInputProps = inputProps || {}
    const requiredText = intl.formatMessage({ id: 'strings.required' })
    if (placeholder && required && outlined) {
      currentInputProps.placeholder = `${placeholder} (${requiredText})`
    } else if (required && outlined) {
      currentInputProps.placeholder = requiredText
    } else if (placeholder && outlined) {
      currentInputProps.placeholder = placeholder
    }
    if (type === 'number') {
      currentInputProps.onWheel = handleWheel
    }
    if (testId) {
      currentInputProps['data-testid'] = testId
    }
    if (maxLength !== undefined && currentInputProps.maxLength === undefined) {
      currentInputProps.maxLength = maxLength
    }

    return currentInputProps
  }

  const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => (event.currentTarget as HTMLInputElement).blur()

  // Normalize styled unicode (e.g. 𝗕𝗼𝗹𝗱, 𝘐𝘵𝘢𝘭𝘪𝘤) in pasted text only. insertText goes through the browser's
  // native editing path, so maxLength, undo and react-hook-form's onChange behave like a regular paste.
  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const pastedText = event.clipboardData.getData('text/plain')
    const normalizedText = pastedText.normalize('NFKC')
    if (normalizedText === pastedText) {
      return
    }

    event.preventDefault()
    document.execCommand('insertText', false, normalizedText)
  }

  return (
    <ContainerVariant fullWidth={fullWidth || true} margin={'normal'} required={required} className={className}>
      <LabelVariant htmlFor={name} data-shrink="true" disabled={disabled}>
        {label}
      </LabelVariant>
      <InputVariant
        name={name}
        type={type}
        {...(register ? register(name) : {})}
        autoComplete={autoComplete}
        required={required}
        inputProps={getInputProps()}
        autoFocus={autoFocus}
        disabled={disabled}
        defaultValue={value}
        sx={{ opacity: disabled ? 0.8 : 1 }}
        onPaste={handlePaste}
      />
      {maxCharactersSoftLimit && (
        <CharacterCount
          disabled={disabled}
          $exceededLimit={maxCharactersSoftLimit !== undefined && currentValue.length > maxCharactersSoftLimit}
        >
          {maxCharactersSoftLimit !== undefined
            ? `${currentValue.length} / ${maxCharactersSoftLimit}`
            : `${currentValue.length}`}
        </CharacterCount>
      )}
    </ContainerVariant>
  )
}

const InputContainer = styled(FormControl)`
  margin: 20px 0 0;
`

const FieldLabel = styled(InputLabel)`
  padding: 0;
  margin-top: -20px;
  transition: none;
  transform: none;
`

const CharacterCount = styled.div<{ disabled?: boolean; $exceededLimit: boolean }>`
  padding: 8px 0;
  text-align: right;
  font-size: 12px;
  color: ${(props) => (props.$exceededLimit ? props.theme.palette.error.main : props.theme.customPalette.darkGray)};
  font-weight: ${(props) => (props.$exceededLimit ? 'bold' : 'normal')};
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
`
